import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import type { DoctorIntelligenceRow } from "@/api/intelligence";
import { formatCount, formatInr } from "@/lib/intelligenceFormat";
import { IntelligenceVariance } from "./IntelligenceVariance";
import { ArrowUpDown } from "lucide-react";
import { cn } from "@/lib/utils";

type SortKey = keyof Pick<
	DoctorIntelligenceRow,
	| "name"
	| "revenue"
	| "patientVolume"
	| "opdVisits"
	| "ipdAdmissions"
	| "surgeries"
	| "revenueVariancePct"
	| "revenueContributionPct"
>;

type Props = {
	doctors: DoctorIntelligenceRow[];
	onSelectDoctor: (doctorId: string) => void;
	selectedDoctorId?: string | null;
	/** When true, rows are shown in API order (server pagination/sort). */
	serverSorted?: boolean;
};

export function DoctorPerformanceTable({
	doctors,
	onSelectDoctor,
	selectedDoctorId,
	serverSorted = false,
}: Props) {
	const [search, setSearch] = useState("");
	const [sortKey, setSortKey] = useState<SortKey>("revenue");
	const [sortAsc, setSortAsc] = useState(false);

	const filtered = useMemo(() => {
		const q = search.trim().toLowerCase();
		let rows = doctors;
		if (q) {
			rows = rows.filter(
				(d) =>
					d.name.toLowerCase().includes(q) ||
					(d.departmentName ?? "").toLowerCase().includes(q) ||
					(d.specialisation ?? "").toLowerCase().includes(q)
			);
		}
		if (serverSorted) return rows;
		return [...rows].sort((a, b) => {
			const av = a[sortKey];
			const bv = b[sortKey];
			if (typeof av === "string" && typeof bv === "string") {
				return sortAsc ? av.localeCompare(bv) : bv.localeCompare(av);
			}
			const na = Number(av ?? 0);
			const nb = Number(bv ?? 0);
			return sortAsc ? na - nb : nb - na;
		});
	}, [doctors, search, sortKey, sortAsc, serverSorted]);

	const toggleSort = (key: SortKey) => {
		if (sortKey === key) setSortAsc(!sortAsc);
		else {
			setSortKey(key);
			setSortAsc(false);
		}
	};

	const SortHead = ({ label, field }: { label: string; field: SortKey }) => (
		<TableHead
			className={cn(
				"whitespace-nowrap",
				field !== "name" && "text-right",
				!serverSorted && "cursor-pointer"
			)}
			onClick={serverSorted ? undefined : () => toggleSort(field)}
		>
			<span className="inline-flex items-center gap-1">
				{label}
				{!serverSorted && <ArrowUpDown className="h-3 w-3 opacity-40" />}
			</span>
		</TableHead>
	);

	return (
		<div className="space-y-3">
			<Input
				placeholder="Search doctor, department, specialisation…"
				value={search}
				onChange={(e) => setSearch(e.target.value)}
				className="max-w-md"
			/>
			<div className="rounded-lg border overflow-x-auto">
				<Table>
					<TableHeader>
						<TableRow>
							<SortHead label="Doctor" field="name" />
							<TableHead>Department</TableHead>
							<SortHead label="Patients" field="patientVolume" />
							<SortHead label="OPD" field="opdVisits" />
							<SortHead label="IPD" field="ipdAdmissions" />
							<TableHead className="text-right">OPD→IPD %</TableHead>
							<SortHead label="Surgeries" field="surgeries" />
							<SortHead label="Revenue" field="revenue" />
							<TableHead className="text-right">Rev / Patient</TableHead>
							<SortHead label="Share %" field="revenueContributionPct" />
							<SortHead label="Variance %" field="revenueVariancePct" />
						</TableRow>
					</TableHeader>
					<TableBody>
						{filtered.length === 0 ? (
							<TableRow>
								<TableCell colSpan={11} className="text-center text-muted-foreground py-8">
									No doctors with activity in this period
								</TableCell>
							</TableRow>
						) : (
							filtered.map((d) => (
								<TableRow
									key={d.doctorId}
									className={cn(
										"cursor-pointer",
										selectedDoctorId === d.doctorId && "bg-indigo-50/60"
									)}
									onClick={() => onSelectDoctor(d.doctorId)}
								>
									<TableCell className="font-medium">{d.name}</TableCell>
									<TableCell className="text-sm text-muted-foreground">
										{d.departmentName ?? "—"}
									</TableCell>
									<TableCell className="text-right tabular-nums">{formatCount(d.patientVolume)}</TableCell>
									<TableCell className="text-right tabular-nums">{formatCount(d.opdVisits)}</TableCell>
									<TableCell className="text-right tabular-nums">{formatCount(d.ipdAdmissions)}</TableCell>
									<TableCell className="text-right tabular-nums">
										{d.opdToIpdConversionPct != null
											? `${d.opdToIpdConversionPct.toFixed(1)}%`
											: "—"}
									</TableCell>
									<TableCell className="text-right tabular-nums">{formatCount(d.surgeries)}</TableCell>
									<TableCell className="text-right tabular-nums font-medium">{formatInr(d.revenue)}</TableCell>
									<TableCell className="text-right tabular-nums text-sm">
										{d.revenuePerPatient != null ? formatInr(d.revenuePerPatient) : "—"}
									</TableCell>
									<TableCell className="text-right tabular-nums">
										{d.revenueContributionPct != null
											? `${d.revenueContributionPct.toFixed(1)}%`
											: "—"}
									</TableCell>
									<TableCell className="text-right">
										<IntelligenceVariance
											percent={d.revenueVariancePct}
											trend={d.trend}
										/>
									</TableCell>
								</TableRow>
							))
						)}
					</TableBody>
				</Table>
			</div>
		</div>
	);
}
